<script lang="ts">
	// T25: Identify a printer (contained Sheet), UC-49 (#518).
	//
	// A printer of the till's own setup that the till cannot tell from another
	// "needs identifying": nothing is sent to it. The store lists the found
	// printers that could be it (`couldBe`). A manager picks the one they mean:
	//   Step 1: the could-be printers in a table, address and model, each with
	//           "Print test slip" (`printer-identify-slip-<id>`). A USB printer
	//           shows, where an address would, "USB printer on this till: <queue>"
	//           on Windows and "USB printer on this till" on Android (#623).
	//           None to pick: a note says to check the printer or set it up
	//           again by address.
	//   Step 2: Sheet Back returns to step 1. The address (a USB printer's own
	//           words), the code in mono
	//           (`printer-identify-code`), "Did this code print?", Yes, this is
	//           <name> (`printer-identify-yes`) and No (`printer-identify-no`).
	//           Answering No shows a flag Band and returns to step 1 on the next
	//           tap.
	// Yes saves the printer with that found printer (a manager's save).
	//
	// A FORBIDDEN, stale or other refusal is a flag Band at the top.

	import { untrack } from 'svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Row from '../components/Row.svelte';
	import Sheet from '../components/Sheet.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import {
		ANDROID_USB,
		USB_KIND,
		couldBeFor,
		identifyPrinter,
		requestMeshTestSlip,
		watchJob,
		type CouldBePrinter,
		type Printer,
		type Refusal,
	} from '../till/printers.svelte';

	interface Props {
		open: boolean;
		language: Language;
		printer: Printer | null;
		onClose: () => void;
		onSaved: () => void;
	}

	let { open, language, printer, onClose, onSaved }: Props = $props();

	let step = $state<1 | 2>(1);
	let picked = $state<CouldBePrinter | null>(null);
	let code = $state('');
	let printingId = $state('');
	let failReason = $state('');
	let answeredNo = $state(false);
	let saving = $state(false);
	let refusal = $state<Refusal | null>(null);

	const candidates = $derived(printer ? couldBeFor(printer.id) : []);

	// A USB printer has no address: it is a printer plugged into this till,
	// named by its queue on Windows (#623).
	function usbPlace(candidate: CouldBePrinter): string | null {
		if (candidate.kind === USB_KIND) {
			return fill(message(language, 'printerUsbOnThisTill'), candidate.locator);
		}
		if (candidate.kind === ANDROID_USB) return message(language, 'printerUsbOptionAnon');
		return null;
	}

	let slipAbort: AbortController | null = null;

	// A watch of a test slip ends with the sheet, even when the sheet goes away open.
	$effect(() => () => slipAbort?.abort());

	$effect(() => {
		if (open) {
			untrack(() => {
				step = 1;
				picked = null;
				code = '';
				printingId = '';
				failReason = '';
				answeredNo = false;
				saving = false;
				refusal = null;
			});
		} else {
			slipAbort?.abort();
			slipAbort = null;
		}
	});

	async function printSlip(candidate: CouldBePrinter) {
		if (printingId) return;
		printingId = candidate.id;
		failReason = '';
		refusal = null;
		answeredNo = false;
		slipAbort?.abort();
		const controller = new AbortController();
		slipAbort = controller;
		try {
			const outcome = await requestMeshTestSlip(candidate.id);
			if (controller.signal.aborted) return;
			if (outcome.kind === 'refused') {
				refusal = outcome;
				printingId = '';
				return;
			}
			code = outcome.job.code ?? '';
			const result = await watchJob(outcome.job.id, controller.signal);
			if (controller.signal.aborted) return;
			printingId = '';
			if (result.kind === 'printed') {
				picked = candidate;
				step = 2;
			} else if (result.kind === 'failed') {
				failReason = result.failReason ?? 'unreachable';
			}
		} catch {
			printingId = '';
			refusal = { kind: 'refused', code: 'failed' };
		}
	}

	async function handleYes() {
		if (saving || !printer || !picked) return;
		saving = true;
		refusal = null;
		const outcome = await identifyPrinter(printer.id, picked.id);
		saving = false;
		if (outcome.kind === 'saved') {
			onSaved();
		} else {
			refusal = outcome;
		}
	}

	function handleBack() {
		step = 1;
		answeredNo = false;
		picked = null;
	}

	const title = $derived(
		printer ? fill(message(language, 'printerIdentifyTitle'), printer.name) : '',
	);

	const slipError = $derived(
		failReason === ''
			? ''
			: failReason === 'other_printer'
				? message(language, 'printerSlipOtherPrinter')
				: message(language, 'printerSlipUnreachable'),
	);
</script>

<Sheet
	{open}
	contain
	level={1}
	{title}
	titleTestId="printer-identify-title"
	backLabel={step === 2 ? message(language, 'printerIdentifyBack') : undefined}
	onBack={step === 2 ? handleBack : undefined}
	backTestId="printer-identify-back"
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="printer-identify-close"
	testId="printer-identify-sheet"
>
	<div class="sheet-body" data-scroll="">
		{#if refusal?.code === 'forbidden'}
			<Band tone="flag" alert testId="printer-identify-error">
				{message(language, 'printerForbidden')}
			</Band>
		{:else if refusal?.code === 'stale'}
			<Band tone="flag" alert testId="printer-identify-error">
				{message(language, 'printerSetupStale')}
			</Band>
		{:else if refusal}
			<Band tone="flag" testId="printer-identify-error">
				{message(language, 'printerFailed')}
			</Band>
		{/if}

		{#if step === 1}
			<p class="intro">{message(language, 'printerIdentifyIntro')}</p>
			{#if slipError}
				<Band tone="flag" testId="printer-identify-slip-error">{slipError}</Band>
			{/if}
			{#if candidates.length === 0}
				<p class="note" data-testid="printer-identify-none">
					{message(language, 'printerIdentifyNone')}
				</p>
			{:else}
				<TablePanel
					columns={[
						{ label: message(language, 'printerAddressLabel'), w: 'minmax(0, 1fr)' },
						{ label: message(language, 'printerModelLabel'), w: 'minmax(0, 1fr)' },
						{ label: '', w: 'minmax(0, 1.2fr)' },
					]}
					testId="printer-identify-list"
				>
					{#each candidates as candidate (candidate.id)}
						<Row testId="printer-identify-row-{candidate.id}">
							{#if usbPlace(candidate) !== null}
								<span data-testid="printer-identify-usb-{candidate.id}">{usbPlace(candidate)}</span>
							{:else}
								<bdi dir="ltr" class="mono">{candidate.locator}</bdi>
							{/if}
							<span class="soft">
								{candidate.evidence?.model || message(language, 'printerModelUnknown')}
							</span>
							<span class="actions">
								<Button
									testId="printer-identify-slip-{candidate.id}"
									icon="printer"
									label={message(language, 'printerTestSlip')}
									tone="secondary"
									size="sm"
									disabled={printingId !== ''}
									state={printingId === candidate.id ? 'pending' : 'idle'}
									pendingLabel={message(language, 'printerTestSlipPrinting')}
									onPress={() => void printSlip(candidate)}
								/>
							</span>
						</Row>
					{/each}
				</TablePanel>
			{/if}
		{:else if picked}
			<div class="step-code">
				<p class="intro">
					{#if usbPlace(picked) !== null}
						{message(language, 'printerCodeIntroUsb')}
					{:else}
						{message(language, 'printerCodeIntro').split('%1')[0]}<bdi dir="ltr" class="mono"
							>{picked.locator}</bdi
						>{message(language, 'printerCodeIntro').split('%1')[1] ?? ''}
					{/if}
				</p>
				<span dir="ltr" class="code-large" data-testid="printer-identify-code">{code}</span>
				{#if !answeredNo}
					<p class="question">{message(language, 'printerCodeQuestion')}</p>
					<div class="choice-actions">
						<Button
							testId="printer-identify-yes"
							icon="check"
							label={fill(message(language, 'printerIdentifyYes'), printer?.name ?? '')}
							tone="primary"
							size="lg"
							disabled={saving}
							state={saving ? 'pending' : 'idle'}
							pendingLabel={message(language, 'saving')}
							onPress={() => void handleYes()}
						/>
						<Button
							testId="printer-identify-no"
							label={message(language, 'printerNo')}
							tone="secondary"
							size="lg"
							disabled={saving}
							onPress={() => (answeredNo = true)}
						/>
					</div>
				{:else}
					<Band tone="flag" testId="printer-identify-not-printed">
						{message(language, 'printerIdentifyNotThis')}
					</Band>
					<div class="choice-actions">
						<Button
							testId="printer-identify-another"
							label={message(language, 'printerIdentifyAnother')}
							tone="primary"
							size="lg"
							onPress={handleBack}
						/>
					</div>
				{/if}
			</div>
		{/if}
	</div>
</Sheet>

<style>
	.sheet-body {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
		overflow-y: auto;
		overflow-x: hidden;
	}

	.intro,
	.note {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.soft {
		color: var(--text-body);
	}

	.mono {
		font-family: var(--font-mono);
	}

	.actions {
		display: flex;
		justify-content: flex-end;
	}

	.step-code {
		max-inline-size: var(--measure);
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-7);
	}

	.code-large {
		font-family: var(--font-mono);
		font-size: var(--figure-xl);
		font-weight: 700;
		line-height: 1;
		letter-spacing: var(--tracking-tight);
		color: var(--text-strong);
	}

	.question {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 700;
		color: var(--text-strong);
	}

	.choice-actions {
		display: flex;
		gap: var(--touch-gap);
		flex-wrap: wrap;
	}
</style>
